import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import PreviewNoPHIBanner, { PREVIEW_MODE } from "../components/PreviewNoPHIBanner";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Trash2 } from "lucide-react";

const FIELDS = [
  { name: "name", label: "Patient name", required: true, type: "text" },
  { name: "dob", label: "Date of birth", type: "date" },
  { name: "sex", label: "Sex", type: "select", options: ["", "Female", "Male", "Other", "Prefer not to say"] },
  { name: "asa_classification", label: "ASA classification", type: "select", options: ["", "ASA I", "ASA II", "ASA III", "ASA IV"] },
  { name: "blood_pressure", label: "Blood pressure (e.g. 128/82)", type: "text" },
  { name: "smoking_status", label: "Smoking status", type: "select", options: ["", "Non-smoker", "Former smoker", "<10 / day", ">10 / day"] },
  { name: "medical_conditions", label: "Medical conditions", type: "textarea", placeholder: "Diabetes, cardiac history, autoimmune, etc." },
  { name: "allergies", label: "Allergies", type: "textarea", placeholder: "Drug, latex, food" },
  { name: "medications", label: "Current medications", type: "textarea" },
  { name: "anticoagulants", label: "Anticoagulants / antiplatelets", type: "text" },
  { name: "mronj_history", label: "Bisphosphonate / MRONJ risk", type: "text" },
  { name: "notes", label: "Notes", type: "textarea" },
];

export default function PatientForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const editing = Boolean(id);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) return;
    api
      .get(`/patients/${id}`)
      .then((r) => setForm(r.data))
      .catch(() => toast.error("Could not load patient"))
      .finally(() => setLoading(false));
  }, [id, editing]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) {
      toast.error("Patient name is required");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/patients/${id}`, form);
        toast.success("Patient updated");
        nav(`/patients/${id}`);
      } else {
        const r = await api.post("/patients", form);
        toast.success("Patient created");
        nav(`/patients/${r.data.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!editing) return;
    if (!window.confirm("Delete this patient? Imaging and case history will remain in storage.")) return;
    await api.delete(`/patients/${id}`);
    toast.success("Deleted");
    nav("/patients");
  };

  if (loading)
    return (
      <Layout>
        <div className="text-[var(--pa-text-muted)]">Loading…</div>
      </Layout>
    );

  // Preview / no-PHI guardrail: never expose an editable real-patient form.
  // We render a demonstration-only shell (disabled inputs, clearly fictional
  // placeholders) plus the no-PHI banner and a disabled Save action.
  if (PREVIEW_MODE) {
    return (
      <Layout>
        <div className="max-w-3xl">
          <Link
            to="/patients"
            className="inline-flex items-center gap-1 text-sm text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] transition mb-4"
          >
            <ArrowLeft size={14} /> Back to Patients
          </Link>
          <div className="mb-6">
            <PreviewNoPHIBanner />
          </div>
          <div className="kicker">Patient Records</div>
          <h1
            className="font-serif text-3xl md:text-4xl text-[var(--pa-text)] mt-2"
            data-testid="patient-form-preview-title"
          >
            Patient workflow — demonstration only
          </h1>
          <p className="mt-2 text-sm text-[var(--pa-text-muted)]">
            The fields below show the information ProsthoAI collects for each
            patient in Production. In Preview they are disabled — nothing you
            type will be saved, transmitted, or stored.
          </p>
          <form
            className="mt-6 grid sm:grid-cols-2 gap-4"
            onSubmit={(e) => e.preventDefault()}
            aria-disabled="true"
          >
            {FIELDS.map((f) => (
              <div key={f.name} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                <label className="block text-xs kicker text-[var(--pa-text-dim)] mb-1">
                  {f.label}
                </label>
                {f.type === "textarea" ? (
                  <textarea
                    rows={3}
                    disabled
                    readOnly
                    value=""
                    placeholder="Demonstration only — no real patient data"
                    data-testid={`patient-field-preview-${f.name}`}
                    className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text-dim)] text-sm p-2.5 opacity-60 cursor-not-allowed"
                  />
                ) : f.type === "select" ? (
                  <select
                    disabled
                    value=""
                    data-testid={`patient-field-preview-${f.name}`}
                    className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text-dim)] text-sm p-2.5 opacity-60 cursor-not-allowed"
                  >
                    <option>Demonstration only</option>
                  </select>
                ) : (
                  <input
                    type="text"
                    disabled
                    readOnly
                    value=""
                    placeholder="Demonstration only — no real patient data"
                    data-testid={`patient-field-preview-${f.name}`}
                    className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text-dim)] text-sm p-2.5 opacity-60 cursor-not-allowed"
                  />
                )}
              </div>
            ))}
            <div className="sm:col-span-2 flex items-center gap-3 mt-2">
              <button
                type="button"
                disabled
                aria-disabled="true"
                data-testid="patient-form-preview-save-disabled"
                className="bg-[var(--pa-bg-2)] border border-[var(--pa-border)] text-[var(--pa-text-dim)] font-semibold px-5 py-2.5 rounded-md cursor-not-allowed"
                title="Preview — saving disabled"
              >
                Preview — saving disabled
              </button>
              <Link
                to="/patients"
                className="text-sm text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] transition"
              >
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </Layout>
    );
  }

  const render = (f) => {
    const v = form[f.name] ?? "";
    const cn =
      "w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] text-sm focus:border-[var(--pa-gold)] focus:outline-none p-2.5";
    if (f.type === "textarea")
      return (
        <textarea
          rows={3}
          value={v}
          onChange={(e) => setField(f.name, e.target.value)}
          placeholder={f.placeholder}
          data-testid={`patient-field-${f.name}`}
          className={cn}
        />
      );
    if (f.type === "select")
      return (
        <select
          value={v}
          onChange={(e) => setField(f.name, e.target.value)}
          data-testid={`patient-field-${f.name}`}
          className={cn}
        >
          {f.options.map((o) => (
            <option key={o} value={o}>
              {o || "Select…"}
            </option>
          ))}
        </select>
      );
    return (
      <input
        type={f.type}
        value={v}
        onChange={(e) => setField(f.name, e.target.value)}
        data-testid={`patient-field-${f.name}`}
        className={cn}
      />
    );
  };

  return (
    <Layout>
      <Link
        to={editing ? `/patients/${id}` : "/patients"}
        className="inline-flex items-center gap-1 text-sm text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] transition"
      >
        <ArrowLeft size={14} /> Back
      </Link>
      <div className="kicker mt-4">{editing ? "Edit Patient" : "New Patient"}</div>
      <h1 className="font-serif text-3xl md:text-4xl text-[var(--pa-text)] mt-2" data-testid="patient-form-title">
        {editing ? form.name : "Create patient record"}
      </h1>

      <div className="gold-divider my-8" />

      <form
        onSubmit={submit}
        data-testid="patient-form"
        className="grid sm:grid-cols-2 gap-5 max-w-3xl"
      >
        {FIELDS.map((f) => (
          <div key={f.name} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
            <label className="block text-xs font-semibold text-[var(--pa-text-muted)] mb-1.5">
              {f.label} {f.required && <span className="text-[var(--pa-gold)]">*</span>}
            </label>
            {render(f)}
          </div>
        ))}
        <div className="sm:col-span-2 flex items-center gap-3 mt-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="patient-form-submit"
            className="bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-6 py-3 rounded-md hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-50"
          >
            {saving ? "Saving…" : editing ? "Save changes" : "Create patient"}
          </button>
          {editing && (
            <button
              type="button"
              onClick={del}
              data-testid="patient-form-delete"
              className="flex items-center gap-1.5 text-sm text-red-400 hover:text-red-300 ml-auto"
            >
              <Trash2 size={14} /> Delete patient
            </button>
          )}
        </div>
      </form>
    </Layout>
  );
}
